/**
 * useUserProfile Hook - Provides access to user profile state and operations
 * Wrapper around UserProfileContext for convenient component usage
 */

import { useUserProfileContext } from '../contexts/UserProfileContext';
import type { UserProfile } from '../types/user';

/**
 * Return type for useUserProfile hook
 */
export interface UseUserProfileReturn {
  /** Current user profile data */
  profile: UserProfile | null;
  /** Loading state for profile operations */
  isLoading: boolean;
  /** Error state for profile operations */
  error: Error | null;
  /** Refresh profile data from backend */
  refreshProfile: () => Promise<void>;
  /** Update profile data */
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
}

/**
 * Custom hook for accessing user profile
 * Provides user profile state and operations from UserProfileContext
 * 
 * Features:
 * - Access to current user profile data
 * - Loading and error states
 * - Refresh profile from backend
 * - Update profile data
 * - Automatic refresh on authentication state change (handled by context)
 * 
 * Must be used within UserProfileProvider component tree
 * 
 * @returns User profile state and operations
 * @throws Error if used outside UserProfileProvider
 * 
 * @example
 * ```tsx
 * function ProfileComponent() {
 *   const { profile, isLoading, error, refreshProfile, updateProfile } = useUserProfile();
 * 
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *   if (!profile) return <div>No profile</div>;
 * 
 *   return (
 *     <div>
 *       <h1>{profile.name}</h1>
 *       <p>{profile.email}</p>
 *       <button onClick={refreshProfile}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useUserProfile(): UseUserProfileReturn {
  const context = useUserProfileContext();

  return {
    profile: context.profile,
    isLoading: context.isLoading,
    error: context.error,
    refreshProfile: context.refreshProfile,
    updateProfile: context.updateProfile,
  };
}
